import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import TopBar from "../../components/TopBar";
import { api, fmtUSD, fmtNum } from "../../lib/api";
import { useYear } from "../../context/YearContext";
import { ChevronLeft, Truck, BarChart3, DollarSign, TrendingDown, TrendingUp, MapPin } from "lucide-react";

export default function FleetUnitDetail() {
  const { unitId } = useParams();
  const { year } = useYear();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/fleet-units/${unitId}/pnl?year=${year}`);
      setData(data); setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load unit");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [unitId, year]);

  if (err) return <div className="p-6 text-[#BC4727] font-bold">{err}</div>;
  if (!data) return <div className="p-6 text-[#66645E]">Loading…</div>;

  const u = data.unit;
  const maxExpense = Math.max(1, ...data.expenses_by_category.map((c) => c.amount));
  const maxMonth = Math.max(1, ...data.monthly_trend.map((m) => Math.max(m.revenue, m.cost)));

  return (
    <div>
      <TopBar
        title={u.nickname}
        subtitle={`${[u.year, u.make, u.model].filter(Boolean).join(" ")} · ${u.kind} · ${u.status}`}
        actions={
          <button
            data-testid="fleet-detail-back"
            onClick={() => navigate(-1)}
            className="border border-[#1F1E1B] px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#1F1E1B] hover:text-white inline-flex items-center gap-1 mt-6"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
        }
      />

      {/* Headline KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="fleet-detail-kpis">
        <Kpi label="Revenue" value={fmtUSD(data.revenue)} tone="positive" />
        <Kpi label="Expenses" value={fmtUSD(data.expenses)} tone="negative" />
        <Kpi label="Profit" value={fmtUSD(data.profit)} tone={data.profit >= 0 ? "highlight" : "negative"} testid="fleet-detail-profit" />
        <Kpi label="Net / Mile"
             value={fmtUSD(data.net_per_mile)}
             tone={data.net_per_mile >= 0 ? "highlight" : "negative"}
             sub={`${fmtUSD(data.revenue_per_mile)} rev − ${fmtUSD(data.cost_per_mile)} cost`}
             testid="fleet-detail-net-per-mile" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <SmallStat label="Loads" value={data.load_count} />
        <SmallStat label="Miles Billed" value={fmtNum(data.miles_billed, 0)} />
        <SmallStat label="Cost / Mile" value={fmtUSD(data.cost_per_mile)} tone="warning" />
        <SmallStat label="Revenue / Mile" value={fmtUSD(data.revenue_per_mile)} tone="positive" />
      </div>

      {/* Expense category breakdown */}
      <div className="bg-white border border-[#1F1E1B] p-5 mb-6" data-testid="fleet-detail-expense-breakdown">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="font-display text-lg font-bold uppercase tracking-tight flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Where the cost goes</h3>
          <span className="text-xs text-[#66645E]">{year} · {data.expenses_by_category.length} categor{data.expenses_by_category.length === 1 ? "y" : "ies"}</span>
        </div>
        {data.expenses_by_category.length === 0 ? (
          <div className="text-sm text-[#66645E] border border-dashed border-[#1F1E1B] p-4 text-center">No expenses logged against this unit yet.</div>
        ) : (
          <div className="space-y-2">
            {data.expenses_by_category.map((c) => {
              const pct = Math.round((c.amount / maxExpense) * 100);
              const totalPct = data.expenses > 0 ? Math.round((c.amount / data.expenses) * 100) : 0;
              return (
                <div key={c.category} className="grid grid-cols-12 gap-3 items-baseline" data-testid={`fleet-expense-cat-${c.category}`}>
                  <div className="col-span-3 text-xs uppercase tracking-wider text-[#1F1E1B] font-bold truncate">{c.category}</div>
                  <div className="col-span-6 bg-[#F5F4F0] h-4 relative">
                    <div className="h-full bg-[#BC4727]" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="col-span-2 text-right tabular text-sm font-bold">{fmtUSD(c.amount)}</div>
                  <div className="col-span-1 text-right text-[10px] text-[#66645E] tabular">{totalPct}%</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Monthly trend */}
      {data.monthly_trend.length > 0 && (
        <div className="bg-white border border-[#1F1E1B] p-5 mb-6" data-testid="fleet-detail-monthly-trend">
          <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-3">Monthly trend</h3>
          <div className="overflow-x-auto">
            <div className="flex items-end gap-3 min-w-max h-44 pr-3">
              {data.monthly_trend.map((m) => {
                const rh = (m.revenue / maxMonth) * 100;
                const ch = (m.cost / maxMonth) * 100;
                return (
                  <div key={m.month} className="flex flex-col items-center gap-1" style={{ minWidth: 60 }} data-testid={`fleet-month-${m.month}`}>
                    <div className="h-32 flex items-end gap-1">
                      <div className="w-5 bg-[#1A4135]" style={{ height: `${Math.max(2, rh)}%` }} title={`Revenue: ${fmtUSD(m.revenue)}`} />
                      <div className="w-5 bg-[#BC4727]" style={{ height: `${Math.max(2, ch)}%` }} title={`Cost: ${fmtUSD(m.cost)}`} />
                    </div>
                    <div className={`text-[11px] font-bold tabular ${m.profit >= 0 ? "text-[#1A4135]" : "text-[#BC4727]"}`}>
                      {fmtUSD(m.profit)}
                    </div>
                    <div className="text-[10px] text-[#66645E] tabular">{m.month}</div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex gap-4 mt-3 text-[10px] uppercase tracking-wider text-[#66645E]">
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-[#1A4135]" /> Revenue</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-[#BC4727]" /> Cost</span>
            <span>Net under each bar</span>
          </div>
        </div>
      )}

      {/* Recent loads & recent expenses */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-[#1F1E1B] p-5" data-testid="fleet-detail-recent-loads">
          <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-3 flex items-center gap-2"><Truck className="w-4 h-4" /> Recent loads ({data.recent_loads.length})</h3>
          {data.recent_loads.length === 0 ? (
            <div className="text-sm text-[#66645E] border border-dashed border-[#1F1E1B] p-4 text-center">No loads recorded.</div>
          ) : (
            <ul className="space-y-1.5">
              {data.recent_loads.map((ld) => (
                <li key={ld.id} className="text-xs border-b border-[#E5E2D6] pb-1.5 last:border-b-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[#66645E] tabular shrink-0">{ld.pickup_date}</span>
                    <span className="truncate">{ld.commodity || "—"}</span>
                    <span className="ml-auto tabular font-bold text-[#1A4135] shrink-0">{fmtUSD(ld.revenue)}</span>
                  </div>
                  <div className="flex items-baseline gap-1 text-[10px] text-[#66645E] mt-0.5">
                    <MapPin className="w-2.5 h-2.5 shrink-0" />
                    <span className="truncate">{ld.pickup_location} → {ld.delivery_location}</span>
                    <span className="ml-auto tabular shrink-0">{fmtNum(ld.miles, 0)} mi</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-[#1F1E1B] p-5" data-testid="fleet-detail-recent-expenses">
          <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-3 flex items-center gap-2"><DollarSign className="w-4 h-4" /> Recent expenses ({data.recent_expenses.length})</h3>
          {data.recent_expenses.length === 0 ? (
            <div className="text-sm text-[#66645E] border border-dashed border-[#1F1E1B] p-4 text-center">No expenses logged.</div>
          ) : (
            <ul className="space-y-1.5">
              {data.recent_expenses.map((e) => (
                <li key={e.id} className="text-xs border-b border-[#E5E2D6] pb-1.5 last:border-b-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[#66645E] tabular shrink-0">{e.date}</span>
                    <span className="text-[9px] uppercase tracking-wider bg-[#F5F4F0] border border-[#D2CEC1] px-1 py-0.5 shrink-0">{e.category}</span>
                    <span className="truncate">{e.vendor || e.description || ""}</span>
                    <span className="ml-auto tabular font-bold text-[#BC4727] shrink-0">{fmtUSD(e.amount)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <Link to="../fleet" className="text-xs uppercase tracking-wider text-[#66645E] hover:text-[#1F1E1B] inline-flex items-center gap-1">
        <ChevronLeft className="w-3 h-3" /> Back to fleet
      </Link>
    </div>
  );
}

function Kpi({ label, value, tone, sub, testid }) {
  const color = tone === "positive" ? "text-[#1A4135]"
              : tone === "negative" ? "text-[#BC4727]"
              : tone === "highlight" ? "text-[#1A4135]"
              : "text-[#1F1E1B]";
  return (
    <div className="bg-white border-2 border-[#1F1E1B] p-4" data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">{label}</div>
      <div className={`font-display text-3xl font-black tabular mt-1 ${color}`}>{value}</div>
      {sub && <div className="text-[11px] text-[#66645E] mt-1">{sub}</div>}
    </div>
  );
}

function SmallStat({ label, value, tone }) {
  const color = tone === "positive" ? "text-[#1A4135]" : tone === "warning" ? "text-[#BC4727]" : "text-[#1F1E1B]";
  return (
    <div className="bg-white border border-[#1F1E1B] p-3">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">{label}</div>
      <div className={`font-display text-xl font-black tabular mt-1 ${color}`}>{value}</div>
    </div>
  );
}
